import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import PopoverMenu from '@deltek/specification-client/core/Navigation/PopoverMenu'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    makeStyles,
    MenuItem,
    PopoverOrigin,
    Theme,
    Typography,
} from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { useIsNotesMenuDisplayed } from 'components/WorkspaceEditor/HoveredItemStore'
import { WorkspaceActionBarContext } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarContext'
import React, { useMemo } from 'react'

export interface MenuItemStyleProps {
    hasSmallTitle?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    actionLabel: {
        fontSize: pxToRem(13),
        color: (props: MenuItemStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#424242' : '#A78966',
    },
    actionMenuItem: {
        padding: (props: MenuItemStyleProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? theme.spacing(0.625, 0)
                : theme.spacing(0.5, 0),
        paddingRight: ({ hasSmallTitle }: MenuItemStyleProps) =>
            hasSmallTitle ? theme.spacing(6) : 'none',
    },
    smallLabel: {
        position: 'absolute',
        right: 0,
        fontSize: theme.spacing(1),
        color: theme.palette.grey[700],
    },
    menuText: {
        fontSize: pxToRem(12),
        color: '#211F20',
        cursor: 'pointer',
        marginTop: -theme.spacing(0.5),
    },
}))

export interface CompleteMenuItemConfig extends MenuItemConfig {
    isDisabled?: boolean
    isHidden?: boolean
    smallLabel?: string
}

/**
 * Props for the Workspace2Popover component.
 */
export interface Workspace2PopoverProps {
    /** The HTML element to which the popover is anchored. */
    anchorElement: Element | null
    /** Determines whether the action popover is displayed. */
    isActionPopoverDisplayed: boolean
    /** Optional origin point for the anchor of the popover. */
    anchorOrigin?: PopoverOrigin
    /** Optional custom CSS style for the text within the popover. */
    customTextStyle?: string
    /** Optional origin point for the transformation of the popover. */
    transformOrigin?: PopoverOrigin
    /** Optional flag to disable reviewer-related functionality. */
    disableReviewer?: boolean
    /** Determines whether Best Practices changes are allowed. */
    shouldAllowBPChange: boolean
    /** Callback function triggered when the action popover is clicked. */
    onCloseMainMenu: () => void
    /** Callback function triggered when the Best Practices menu is clicked. */
    onClickBPMenu: (e: any) => void
    /** Callback function triggered when the manage options menu is clicked. */
    onClickManageOptions: (e: any) => void
    /** Callback function triggered when the notes menu is clicked. */
    onClickNotesMenu: () => void
    /** Callback function triggered when the move block is clicked. */
    onClickMoveBlockMenu: (e: any) => void
}
/**
 * Generates the main menu items.
 */
export const getMainMenuItems = (
    classes: any,
    onClickManageOptions: (e: any) => void,
    onClickNotesMenu: () => void,
    onClickBPMenu: (e: any) => void,
    onClickMoveBlockMenu: (e: any) => void
): CompleteMenuItemConfig[] => [
    {
        dataTestId: 'manageOptions',
        label: 'Manage Options',
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={onClickManageOptions}
            >
                <GridItem>
                    <Typography component="span" className={classes.menuText}>
                        Manage Options
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'notes',
        label: 'Notes',
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={onClickNotesMenu}
            >
                <GridItem>
                    <Typography component="span" className={classes.menuText}>
                        Notes
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'bestPractice',
        label: 'Best Practices',
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={onClickBPMenu}
            >
                <GridItem>
                    <Typography component="span" className={classes.menuText}>
                        Best Practices
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'moveblock',
        label: 'Move Block',
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={onClickMoveBlockMenu}
            >
                <GridItem>
                    <Typography component="span" className={classes.menuText}>
                        Move Block
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
            </GridContainer>
        ),
    },
]

/**
 * Generates the notes menu items.
 */
export const getNotesMenuItems = (
    classes: any,
    onClickNotesMenu: () => void
): CompleteMenuItemConfig[] => [
    {
        dataTestId: 'backToMain',
        label: 'Main Menu',
        component: (
            <GridContainer
                justifyContent="flex-start"
                alignItems="center"
                onClick={onClickNotesMenu}
            >
                <GridItem
                    style={{
                        maxHeight: pxToRem(24),
                        left: pxToRem(3),
                        position: 'absolute',
                    }}
                >
                    <ChevronLeftIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
                <GridItem style={{ paddingLeft: 15 }}>
                    <Typography component="span" className={classes.menuText}>
                        Main Menu
                    </Typography>
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'allNotes',
        label: 'All Notes',
        onClick: () => console.log('View All Notes'),
    },
    {
        dataTestId: 'createNote',
        label: 'Create New Note',
        onClick: () => console.log('Create New Note'),
    },
]
export const Workspace2Popover = ({
    anchorElement,
    isActionPopoverDisplayed,
    onCloseMainMenu,
    anchorOrigin,
    customTextStyle,
    transformOrigin,
    disableReviewer,
    onClickBPMenu,
    onClickManageOptions,
    onClickNotesMenu,
    shouldAllowBPChange,
    onClickMoveBlockMenu,
}: Workspace2PopoverProps) => {
    const { currentState } = React.useContext(WorkspaceActionBarContext)
    // checking if the redesigned toggle on show/hide bar is enabled
    const isBetaWorkspaceToggleEnabled =
        processEnv?.REACT_APP_ShowRedesignedEditorToggle === '1' &&
        currentState?.showHideStates?.betaWorkspace
    const classes = useStyles({
        hasSmallTitle: false,
        isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
    })

    const isNotesMenuDisplayed = useIsNotesMenuDisplayed()
    const mainMenuItems = useMemo(
        () =>
            getMainMenuItems(
                classes,
                onClickManageOptions,
                onClickNotesMenu,
                onClickBPMenu,
                onClickMoveBlockMenu
            ),
        [
            classes,
            onClickManageOptions,
            onClickNotesMenu,
            onClickBPMenu,
            onClickMoveBlockMenu,
        ]
    )
    const notesMenuItems = useMemo(
        () => getNotesMenuItems(classes, onClickNotesMenu),
        [classes, onClickNotesMenu]
    )

    const menuItems = useMemo(
        () =>
            isNotesMenuDisplayed
                ? notesMenuItems
                : shouldAllowBPChange
                ? mainMenuItems
                : mainMenuItems.filter(
                      (item) => item.dataTestId !== 'bestPractice'
                  ),
        [
            isNotesMenuDisplayed,
            shouldAllowBPChange,
            mainMenuItems,
            notesMenuItems,
        ]
    )

    return (
        <PopoverMenu
            open={isActionPopoverDisplayed}
            anchorEl={anchorElement}
            onClose={onCloseMainMenu}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            data-testid="workspace2Popover"
        >
            {menuItems.map(
                ({ dataTestId, component, label, onClick, isDisabled }) =>
                    component ?? (
                        <MenuItem
                            key={dataTestId}
                            onClick={onClick as () => void}
                            disabled={isDisabled || disableReviewer}
                            className={clsx(
                                customTextStyle,
                                classes.actionMenuItem,
                                classes.menuText
                            )}
                        >
                            {label}
                        </MenuItem>
                    )
            )}
        </PopoverMenu>
    )
}
